import React, { useRef } from 'react';
import PhoneInput from 'react-phone-input-2';

import { TableContainer } from 'apps/auth/components/common/components/TableRow/styles';
import { TableRow } from 'apps/auth/components/common/components/TableRow/TableRow';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import ActivityAmount from 'apps/auth/components/ga/activity/components/ActivityAmount';
import ActivityIcon from 'apps/auth/components/ga/activity/components/ActivityIcon';
import ActivityStatus from 'apps/auth/components/ga/activity/components/ActivityStatus';
import ActivityTitle from 'apps/auth/components/ga/activity/components/ActivityTitle';
import { STATUS_OK } from 'apps/auth/constants/Constants';
import { INTERNAL_TRANSFER_IN, INTERNAL_TRANSFER_OUT } from 'apps/auth/constants/GaConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { formatMoney } from 'apps/auth/helpers/formats';
import { getGaActivityDate } from 'apps/auth/helpers/utils';
import { useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { useCountryMobileMask } from 'apps/auth/hooks/useCountryMobileMask';
import pallets from 'apps/auth/theme/pallets';

import ValidationPopup from '../popup/ValidationPopup';

const getRepeatURL = (pocketId, payId) => `/auth/ga/pocket/${pocketId}/send/repeat/${payId}/CONTACT`;

const ActivityP2PDetails = (props) => {
    const popupRef = useRef(null);
    const item = props.item;
    const mobileInfo = item.mobileInfo;
    const { textColor, titleColor, failedCheque } = props.options;
    const { theme } = useThemeDetector();
    const checkStatusFailedColor = theme === 'dark' ? pallets.dark.grey_40_lc : pallets.light.grey_40_lc;
    const checkStatusSuccessColor = theme === 'dark' ? pallets.dark.black : pallets.light.black;

    const { lang } = useLanguage();
    const {
        chequeDeliveryHeader,
        chequeRecipientHeader,
        chequeStatusInProgress,
        chequeAccountStatusInProgress,
        chequeStatusWaitingVerification,
        chequeStatusSuccess,
        chequeStatusPaid,
        chequeStatusCanceled,
        chequeStatusError,
        chequeDate,
        chequeFee,
        chequeFxRate,
        chequeFromCardHeader,
        chequeBlockProviderHeader,
        chequeBlockBankHeader,
        toLabel,
        paysend,
        transferToAccount,
        repeatButton,
    } = usePageMessages();
    const { defaultMask, masks, isReady, isMaskReadyForIso2 } = useCountryMobileMask(item.toCountryISO);
    const incomeP2P = mobileInfo.icon === INTERNAL_TRANSFER_IN;
    const outcomeP2P = mobileInfo.icon === INTERNAL_TRANSFER_OUT;

    const getAmount = () => {
        if (mobileInfo.curRate) {
            return (
                <>
                    <ActivityAmount options={props.options} item={item} useArrow />
                    <ActivityAmount options={props.options} item={item} isFrom={false} />
                </>
            );
        }
        return <ActivityAmount options={props.options} item={item} baseColor="primary" />;
    };

    const getStatusText = () => {
        let statusText = chequeStatusInProgress;
        if (item.mobileInfo.stsId < STATUS_OK && item.fromchequeBankAccountStatusBean) {
            statusText = chequeAccountStatusInProgress;
            // <c:set var="cssClassStatus" value="sending"/>
        } else if (item.mobileInfo.stsId < STATUS_OK && item.waitingVerification) {
            statusText = chequeStatusWaitingVerification;
        } else if (item.mobileInfo.stsId === STATUS_OK) {
            statusText = outcomeP2P ? chequeStatusSuccess : chequeStatusPaid;
        } else if (item.mobileInfo.stsId > STATUS_OK && (item.cancellingProcess || item.errorCode === 69)) {
            statusText = chequeStatusCanceled;
        } else if (item.mobileInfo.stsId > STATUS_OK) {
            statusText = chequeStatusError;
        }
        return statusText;
    };

    const clickRepeat = async (e) => {
        e.preventDefault();
        const repeatURL = getRepeatURL(props.pocketId, props.item.payId);
        const json = await apiFetchData(repeatURL);
        if (json?.extra?.invalidBalance) {
            popupRef.current?.open(<ValidationPopup data={json} pocketId={props.pocketId} popupRef={popupRef} />);
        } else {
            window.location.href = repeatURL;
        }
    };

    const getActionButton = () => (
        <Panel isHoverable type="button" onClick={clickRepeat} negativeMargin>
            <DiskBadgeWrapper>
                <DiskBadge variant="primary" iconName="retry" />
            </DiskBadgeWrapper>
            <Text color="primary">{repeatButton}</Text>
        </Panel>
    );

    return (
        <>
            <Row indent="adaptive-16-24">
                <ActivityIcon item={item} options={props.options} flagName="paysend" />
            </Row>
            <ActivityTitle message={mobileInfo.title} color={textColor} />
            <Row indent="adaptive-24-32">
                {getAmount()}
                {item.payComment && (
                    <Text variant="paragraph" ellipsis color="secondary">
                        {item.payComment}
                    </Text>
                )}
            </Row>
            <Row indent={mobileInfo.errorMessage ? 'adaptive-16-24' : null}>
                <ActivityStatus isShowStatus status={mobileInfo.stsId} text={getStatusText()} />
            </Row>
            {mobileInfo.errorMessage && <div>{mobileInfo.errorMessage}</div>}
            {outcomeP2P && (
                <>
                    <Separator marginBottom="16px" />
                    {getActionButton()}
                </>
            )}
            <Separator marginBottom="48px" marginTop={outcomeP2P ? '16px' : '48px'} />
            <TableContainer>
                <TableRow title={chequeDate} textColor={textColor} titleColor={titleColor}>
                    {getGaActivityDate(item.payTs, lang)}
                </TableRow>
                {incomeP2P && (
                    <TableRow title={chequeFromCardHeader} textColor={textColor} titleColor={titleColor}>
                        {toLabel + ' ' + mobileInfo.toCurIso + ' ' + transferToAccount.toLowerCase()}
                    </TableRow>
                )}
                {outcomeP2P && (
                    <TableRow title={chequeFee} textColor={textColor} titleColor={titleColor}>
                        {formatMoney(mobileInfo.feeUp)} {mobileInfo.frCurIso}
                    </TableRow>
                )}
                {mobileInfo.curRate && (
                    <TableRow title={chequeFxRate} textColor={textColor} titleColor={titleColor}>
                        {`${formatMoney(1)} ${mobileInfo.frCurIso} = ${formatMoney(mobileInfo.curRate, 4)} ${mobileInfo.toCurIso}`}
                    </TableRow>
                )}
            </TableContainer>
            {outcomeP2P && (mobileInfo.extraFields?.Recipient || mobileInfo.extraFields.Delivery || mobileInfo.extraFields.Bank) && (
                <>
                    <Separator marginTop="48px" marginBottom="48px" />
                    <TableContainer>
                        {mobileInfo.extraFields?.Recipient && (
                            <TableRow title={chequeRecipientHeader} textColor={textColor} titleColor={titleColor}>
                                {mobileInfo.extraFields.Recipient}
                            </TableRow>
                        )}
                        {mobileInfo.extraFields?.Delivery && (
                            <TableRow title={chequeDeliveryHeader} textColor={textColor} titleColor={titleColor}>
                                <Row flex>
                                    {toLabel}
                                    {isReady && isMaskReadyForIso2 && (
                                        <PhoneInput
                                            specialLabel={''}
                                            value={mobileInfo.extraFields.Delivery}
                                            defaultMask={defaultMask}
                                            masks={masks}
                                            disableDropdown={true}
                                            containerStyle={{ marginLeft: '5px' }}
                                            inputProps={{ readOnly: true }}
                                            inputStyle={{
                                                fontSize: 'inherit',
                                                lineHeight: 'inherit',
                                                fontFamily: 'inherit',
                                                background: 'inherit',
                                                border: 'none',
                                                color: failedCheque ? checkStatusFailedColor : checkStatusSuccessColor,
                                            }}
                                        />
                                    )}
                                </Row>
                            </TableRow>
                        )}
                        <TableRow title={chequeBlockProviderHeader} textColor={textColor} titleColor={titleColor}>
                            {paysend}
                        </TableRow>
                        {mobileInfo.extraFields?.Bank && (
                            <TableRow title={chequeBlockBankHeader} textColor={textColor} titleColor={titleColor}>
                                {mobileInfo.extraFields.Bank}
                            </TableRow>
                        )}
                    </TableContainer>
                </>
            )}
            {outcomeP2P && (
                <>
                    <Separator marginTop="48px" marginBottom="48px" />
                    <TableContainer>
                        <TableRow title={chequeFromCardHeader} textColor={textColor} titleColor={titleColor}>
                            {mobileInfo.frCurIso + ' ' + transferToAccount.toLowerCase()}
                        </TableRow>
                    </TableContainer>
                </>
            )}
            <PopupNew ref={popupRef} />
        </>
    );
};

export default ActivityP2PDetails;
